import type { MarketRich, MarketType, MatchedGame, VenueId } from '@rfqsports/shared';
import { bookKey, topOfBook, type BookState } from './store';

export interface GroupedMarket {
  marketType: MarketType;
  line: number | null;
  // Each side is keyed by venue; absent venue = no listing.
  // For moneyline/spread, sides are 'home' | 'away'.
  // For total, sides are 'over' | 'under'.
  // For YES/NO binaries we surface as a single row.
  rows: PricedRow[];
}

export interface PricedRow {
  label: string;
  polymarket: PricedCell | null;
  kalshi: PricedCell | null;
}

export interface PricedCell {
  venue: VenueId;
  marketId: string;
  outcomeId: string;
  bid: number | null;
  ask: number | null;
  bidSize: number | null;
  askSize: number | null;
  ts: number | null;
  marketSide: string;
  closeTs: number | null;
}

const TYPE_ORDER: MarketType[] = ['moneyline', 'spread', 'total', 'prop', 'other'];

export function impliedProbabilityPct(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price)) return '—';
  return `${Math.round(price * 100)}%`;
}

export function americanOdds(price: number | null | undefined): string {
  if (price == null || !Number.isFinite(price) || price <= 0 || price >= 1) return '—';
  const odds = price >= 0.5
    ? -100 * (price / (1 - price))
    : 100 * ((1 - price) / price);
  const rounded = Math.round(odds);
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}

export function relativeTime(ts: number, now = Date.now()): string {
  const delta = ts - now;
  const abs = Math.abs(delta);
  const day = 24 * 3600_000;
  const hour = 3600_000;
  const min = 60_000;
  const tense = (s: string): string => (delta < 0 ? `${s} ago` : `in ${s}`);
  if (abs < min) return tense('< 1m');
  if (abs < hour) return tense(`${Math.round(abs / min)}m`);
  if (abs < day) {
    const h = Math.floor(abs / hour);
    const m = Math.round((abs - h * hour) / min);
    return tense(m ? `${h}h ${m}m` : `${h}h`);
  }
  const d = Math.floor(abs / day);
  const h = Math.round((abs - d * day) / hour);
  return tense(h ? `${d}d ${h}h` : `${d}d`);
}

export function ageSeconds(ts: number | null | undefined, now = Date.now()): number | null {
  if (ts == null || !Number.isFinite(ts)) return null;
  return Math.max(0, Math.floor((now - ts) / 1000));
}

function buildCell(
  market: MarketRich,
  books: Map<string, BookState>,
): PricedCell {
  const book = books.get(bookKey(market.venue, market.marketId, market.outcomeId));
  const { bid, ask, bidSize, askSize } = topOfBook(book);
  return {
    venue: market.venue,
    marketId: market.marketId,
    outcomeId: market.outcomeId,
    bid,
    ask,
    bidSize,
    askSize,
    ts: book?.ts ?? null,
    marketSide: market.side,
    closeTs: Number.isFinite(market.closeTs) ? market.closeTs : null,
  };
}

function pickHomeAwayLabel(home: string, away: string, side: string): string {
  if (side === 'home') return home;
  if (side === 'away') return away;
  return side.toUpperCase();
}

export function groupGameMarkets(
  game: MatchedGame,
  books: Map<string, BookState>,
): GroupedMarket[] {
  const all: MarketRich[] = [];
  if (game.polymarket) all.push(...game.polymarket.markets);
  if (game.kalshi) all.push(...game.kalshi.markets);

  const byType = new Map<MarketType, MarketRich[]>();
  for (const m of all) {
    const list = byType.get(m.marketType) ?? [];
    list.push(m);
    byType.set(m.marketType, list);
  }

  const out: GroupedMarket[] = [];
  for (const t of TYPE_ORDER) {
    const list = byType.get(t);
    if (!list || !list.length) continue;

    if (t === 'moneyline') {
      out.push({ marketType: t, line: null, rows: buildMoneylineRows(list, books, game) });
      continue;
    }
    if (t === 'total') {
      out.push(...buildTotalGroups(list, books));
      continue;
    }
    if (t === 'spread') {
      out.push(...buildSpreadGroups(list, books, game));
      continue;
    }
    out.push({
      marketType: t,
      line: null,
      rows: buildGenericRows(list, books),
    });
  }
  return out;
}

/**
 * Group markets by their `line` value. Markets with no `line` field collapse
 * into a single `line: null` bucket so we don't lose them when an adapter
 * couldn't extract the number from the title.
 */
function groupByLine(markets: MarketRich[]): Map<number | null, MarketRich[]> {
  const buckets = new Map<number | null, MarketRich[]>();
  for (const m of markets) {
    const key = m.line ?? null;
    const arr = buckets.get(key) ?? [];
    arr.push(m);
    buckets.set(key, arr);
  }
  return buckets;
}

// Defensive guard against sub-period markets ("1H Moneyline", "Q3 Winner")
// sneaking past the adapter's classification and overwriting the full-game
// row. The adapters already strip these to `marketType: 'other'` — but if
// a future Polymarket title shape slips by, we'd rather skip + warn than
// silently display a low-volume half-time book as the headline moneyline.
const SUB_PERIOD_TITLE_RE =
  /\b(1H|2H|H1|H2|Q[1-4]|first half|second half|first quarter|second quarter|third quarter|fourth quarter|halftime|half[- ]time|overtime|\bOT\b)\b/i;

function isSubPeriodTitle(title: string | undefined): boolean {
  return !!title && SUB_PERIOD_TITLE_RE.test(title);
}

function buildMoneylineRows(
  markets: MarketRich[],
  books: Map<string, BookState>,
  game: MatchedGame,
): PricedRow[] {
  // Both adapters now emit side='home'/'away' when team identity is resolvable.
  // The title-fallback below only fires for the rare 'yes' case (Kalshi market
  // whose subtitle didn't resolve to either team).
  const sides: Record<'home' | 'away', { polymarket: PricedCell | null; kalshi: PricedCell | null }> = {
    home: { polymarket: null, kalshi: null },
    away: { polymarket: null, kalshi: null },
  };
  // Remember which MarketRich actually wrote each slot so we can compare on
  // collision and prefer the full-game row (longer closeTs, non-sub-period
  // title) over a sneaky half-time leak.
  const sources: Record<
    'home' | 'away',
    { polymarket: MarketRich | null; kalshi: MarketRich | null }
  > = {
    home: { polymarket: null, kalshi: null },
    away: { polymarket: null, kalshi: null },
  };

  const assign = (
    side: 'home' | 'away',
    venue: VenueId,
    market: MarketRich,
  ): void => {
    const prev = sources[side][venue];
    if (!prev) {
      sources[side][venue] = market;
      sides[side][venue] = buildCell(market, books);
      return;
    }
    // Pick the better candidate. Sub-period titles lose to non-sub-period;
    // tiebreaker = whichever closes later (longer-lived market is more
    // likely the full-game line).
    const prevSub = isSubPeriodTitle(prev.title);
    const nextSub = isSubPeriodTitle(market.title);
    const prevWins = prevSub === nextSub
      ? (prev.closeTs ?? 0) >= (market.closeTs ?? 0)
      : !prevSub && nextSub;
    const winner = prevWins ? prev : market;
    const loser = prevWins ? market : prev;
    if (typeof console !== 'undefined' && console.warn) {
      // Surfacing the collision loudly so a regression is visible in
      // dev + prod browser consoles. The CLAUDE.md WS-debug doc spells
      // out that web-side warnings are unconditional.
      console.warn(
        '[odds] moneyline collision — multiple markets matched same (venue, side):',
        {
          gameId: game.id,
          venue,
          side,
          kept: { marketId: winner.marketId, title: winner.title, closeTs: winner.closeTs },
          dropped: { marketId: loser.marketId, title: loser.title, closeTs: loser.closeTs },
        },
      );
    }
    sources[side][venue] = winner;
    sides[side][venue] = buildCell(winner, books);
  };

  const unresolved: MarketRich[] = [];
  for (const m of markets) {
    if (m.side === 'home' || m.side === 'away') {
      assign(m.side, m.venue, m);
      continue;
    }
    unresolved.push(m);
  }

  // Fallback for legacy/unresolved markets: title contains home or away team name.
  for (const m of unresolved) {
    const t = (m.title ?? '').toLowerCase();
    const home = (game.homeTeam ?? '').toLowerCase();
    const away = (game.awayTeam ?? '').toLowerCase();
    let assigned: 'home' | 'away' | null = null;
    if (home && t.includes(home)) assigned = 'home';
    else if (away && t.includes(away)) assigned = 'away';
    if (!assigned) continue;
    assign(assigned, m.venue, m);
  }

  return [
    {
      label: pickHomeAwayLabel(game.homeTeam, game.awayTeam, 'home'),
      polymarket: sides.home.polymarket,
      kalshi: sides.home.kalshi,
    },
    {
      label: pickHomeAwayLabel(game.homeTeam, game.awayTeam, 'away'),
      polymarket: sides.away.polymarket,
      kalshi: sides.away.kalshi,
    },
  ];
}

function buildTotalGroups(markets: MarketRich[], books: Map<string, BookState>): GroupedMarket[] {
  const buckets = groupByLine(markets);
  const out: GroupedMarket[] = [];
  for (const [line, list] of buckets) {
    const overs: PricedCell[] = [];
    const unders: PricedCell[] = [];
    for (const m of list) {
      const cell = buildCell(m, books);
      if (m.side === 'over') overs.push(cell);
      else if (m.side === 'under') unders.push(cell);
    }
    out.push({
      marketType: 'total',
      line,
      rows: [
        {
          label: line != null ? `Over ${line}` : 'Over',
          polymarket: overs.find((c) => c.venue === 'polymarket') ?? null,
          kalshi: overs.find((c) => c.venue === 'kalshi') ?? null,
        },
        {
          label: line != null ? `Under ${line}` : 'Under',
          polymarket: unders.find((c) => c.venue === 'polymarket') ?? null,
          kalshi: unders.find((c) => c.venue === 'kalshi') ?? null,
        },
      ],
    });
  }
  out.sort((a, b) => (a.line ?? Infinity) - (b.line ?? Infinity));
  return out;
}

function buildSpreadGroups(
  markets: MarketRich[],
  books: Map<string, BookState>,
  game: MatchedGame,
): GroupedMarket[] {
  const buckets = groupByLine(markets);
  const out: GroupedMarket[] = [];
  for (const [line, list] of buckets) {
    const homeCells: PricedCell[] = [];
    const awayCells: PricedCell[] = [];
    for (const m of list) {
      const cell = buildCell(m, books);
      if (m.side === 'home') homeCells.push(cell);
      else if (m.side === 'away') awayCells.push(cell);
    }
    const signed = (side: 'home' | 'away'): string => {
      if (line == null) return side === 'home' ? game.homeTeam : game.awayTeam;
      // Convention: positive `line` means the home team is favored by that many.
      // Home shows the negative spread (favored), away shows the positive.
      const homeSpread = -Math.abs(line);
      const awaySpread = Math.abs(line);
      const fmt = (n: number): string => (n > 0 ? `+${n}` : `${n}`);
      return side === 'home' ? `${game.homeTeam} ${fmt(homeSpread)}` : `${game.awayTeam} ${fmt(awaySpread)}`;
    };
    out.push({
      marketType: 'spread',
      line,
      rows: [
        {
          label: signed('home'),
          polymarket: homeCells.find((c) => c.venue === 'polymarket') ?? null,
          kalshi: homeCells.find((c) => c.venue === 'kalshi') ?? null,
        },
        {
          label: signed('away'),
          polymarket: awayCells.find((c) => c.venue === 'polymarket') ?? null,
          kalshi: awayCells.find((c) => c.venue === 'kalshi') ?? null,
        },
      ],
    });
  }
  out.sort((a, b) => (a.line ?? Infinity) - (b.line ?? Infinity));
  return out;
}

function buildGenericRows(markets: MarketRich[], books: Map<string, BookState>): PricedRow[] {
  // Group by market title; pair across venues where they share a title.
  const byTitle = new Map<string, { polymarket: PricedCell | null; kalshi: PricedCell | null; label: string }>();
  for (const m of markets) {
    const key = (m.title ?? m.marketId).toLowerCase();
    const entry = byTitle.get(key) ?? { polymarket: null, kalshi: null, label: m.title ?? m.marketId };
    if (m.venue === 'polymarket') entry.polymarket = buildCell(m, books);
    else entry.kalshi = buildCell(m, books);
    byTitle.set(key, entry);
  }
  return Array.from(byTitle.values()).map((e) => ({
    label: e.label,
    polymarket: e.polymarket,
    kalshi: e.kalshi,
  }));
}

export function bestAsk(row: PricedRow): { venue: VenueId | null; price: number | null } {
  let best: { venue: VenueId; price: number } | null = null;
  for (const cell of [row.polymarket, row.kalshi]) {
    if (cell?.ask != null) {
      if (!best || cell.ask < best.price) best = { venue: cell.venue, price: cell.ask };
    }
  }
  return best ?? { venue: null, price: null };
}

export function bestBid(row: PricedRow): { venue: VenueId | null; price: number | null } {
  let best: { venue: VenueId; price: number } | null = null;
  for (const cell of [row.polymarket, row.kalshi]) {
    if (cell?.bid != null) {
      if (!best || cell.bid > best.price) best = { venue: cell.venue, price: cell.bid };
    }
  }
  return best ?? { venue: null, price: null };
}

/**
 * Mid-price of a cell: midpoint of bid/ask when both are present, else
 * whichever side is quoted. Returns null when the cell can't be priced.
 */
export function cellMid(cell: PricedCell | null): number | null {
  if (!cell) return null;
  if (cell.bid != null && cell.ask != null) return (cell.bid + cell.ask) / 2;
  if (cell.ask != null) return cell.ask;
  if (cell.bid != null) return cell.bid;
  return null;
}

const midPrice = cellMid;

/**
 * Bid/ask spread in probability points (0-100). Returns null when the cell
 * isn't two-sided. Negative spreads can happen briefly during a flickering
 * book and are clamped to 0.
 */
export function cellSpreadPp(cell: PricedCell | null): number | null {
  if (!cell || cell.bid == null || cell.ask == null) return null;
  return Math.max(0, (cell.ask - cell.bid) * 100);
}

/**
 * Absolute cross-venue mid-price gap for a single row, in [0, 1].
 * Returns null when at least one side is missing a price.
 */
export function rowEdge(row: PricedRow): number | null {
  const pm = midPrice(row.polymarket);
  const k = midPrice(row.kalshi);
  if (pm == null || k == null) return null;
  return Math.abs(pm - k);
}

/**
 * Headline "where the two venues disagree most" for a matched game,
 * looking at the moneyline rows only. Returns null when the game can't
 * be priced on both venues.
 */
export function gameMoneylineEdge(
  game: MatchedGame,
  books: Map<string, BookState>,
): number | null {
  const groups = groupGameMarkets(game, books);
  const ml = groups.find((g) => g.marketType === 'moneyline');
  if (!ml) return null;
  let best: number | null = null;
  for (const row of ml.rows) {
    const e = rowEdge(row);
    if (e == null) continue;
    if (best == null || e > best) best = e;
  }
  return best;
}

export function marketTypeLabel(t: MarketType): string {
  switch (t) {
    case 'moneyline': return 'Moneyline';
    case 'spread': return 'Spread';
    case 'total': return 'Total';
    case 'prop': return 'Props';
    case 'other': return 'Other';
  }
}
